import { useContext, useState, Fragment } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import to from 'await-to-js'
import { useTheme } from '@emotion/react'
import * as yup from 'yup'
import { Formik, Form } from 'formik'
import { isEmpty } from 'lodash'
import { Alert, Button } from 'chaoskit/src/components'
import FormikField from 'chaoskit/src/components/FormikField'
import { misc, text } from 'chaoskit/src/assets/styles/utility'
import qs from 'qs'

import { ReliabilityCalculatorContext } from './Context'
import ReliabilityRecommendations from './ReliabilityRecommendations'
import IntroCopy from './Step11IntroCopy'

import {
  getGrade,
  ReliabilityCell,
  StylesRC,
} from '~partials/reliability-calculator/UI'
import mascotArms from '~media/global/mascot-arms.svg'
import track from '~helpers/track'
import '~helpers/yup-corporate-email'
import BotField from '~components/BotField'

const reliabilityCalculatorSchema = yup.object().shape({
  'bot-field': yup.string().max(0),
  email: yup
    .string()
    .required()
    .email('Please provide a valid email')
    .corporateEmail()
    .trim()
    .label('Email'),
  firstName: yup.string().required().trim().label('First name'),
  lastName: yup.string().required().trim().label('Last name'),
  company: yup.string().required().trim().label('Company'),
  jobTitle: yup.string().required().trim().label('Job title'),
})

const Step11Variables = {
  mascot: {
    width: 115,
    height: 80,
  },
}

const Step11 = ({ queryArgs, location }) => {
  const theme = useTheme()

  const { state } = useContext(ReliabilityCalculatorContext)

  const [response, setResponse] = useState('')

  const renderGrade = () => {
    if (!isEmpty(queryArgs)) {
      const {
        step1,
        step2,
        step3,
        step4,
        step5,
        step6,
        step7,
        step8,
        step9,
      } = queryArgs

      return getGrade(
        step1,
        step2,
        step3,
        step4,
        step5,
        step6,
        step7,
        step8,
        step9
      )
    }

    const {
      step1,
      step2,
      step3,
      step4,
      step5,
      step6,
      step7,
      step8,
      step9,
    } = state

    return getGrade(
      step1,
      step2,
      step3,
      step4,
      step5,
      step6,
      step7,
      step8,
      step9
    )
  }

  const urlResults = window.btoa(
    unescape(encodeURIComponent(qs.stringify(state)))
  )
  const url = `${location.origin}${location.pathname}?results=${urlResults}&ref=calc`

  const formId = 'reliability-calculator'

  const getInitialValues = () => {
    const initialValues = {
      'form-name': formId,
      'bot-field': '',
      email: '',
      firstName: '',
      lastName: '',
      company: '',
      jobTitle: '',
      url,
    }

    return initialValues
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setResponse(null)
    setSubmitting(true)

    const [, success] = await to(
      axios({
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data: qs.stringify(values),
        url: '/',
      })
    )

    setSubmitting(false)

    if (success) {
      track('Reliability grade requested', {
        label: `netlify-${formId}`,
      })

      setResponse('success')
    } else {
      setResponse('error')
    }
  }

  const grade = renderGrade()

  return (
    <ReliabilityCell>
      {response !== 'success' && isEmpty(queryArgs) ? (
        <Fragment>
          <div
            css={{
              position: 'relative',
              paddingTop: Step11Variables.mascot.height / 1.35, // Takes care of offset of mascot

              '&::before': {
                content: "''",
                background: `url(${mascotArms}) center center no-repeat`,
                backgroundSize: 'contain',
                width: Step11Variables.mascot.width,
                height: Step11Variables.mascot.height,
                position: 'absolute',
                top: 0,
                left: theme.space.large,
                zIndex: 2,
              },
            }}
          >
            <div
              css={{
                display: 'grid',
                background: theme.color.panel.base,
                borderRadius: theme.borderRadius.base,
                boxShadow: theme.boxShadow.large,

                [theme.mq.medium]: {
                  gridTemplateColumns: 'auto 1fr',
                },
              }}
            >
              <div
                css={{
                  borderBottom: theme.border.base,
                  textAlign: 'center',
                  background: theme.color.light.base,
                  borderTopLeftRadius: theme.borderRadius.base,
                  borderBottomLeftRadius: theme.borderRadius.base,
                  lineHeight: theme.lineHeight.small,
                  fontWeight: theme.fontWeight.bold,
                  fontSize: 100,
                  padding: theme.space.large,

                  [theme.mq.medium]: {
                    borderBottom: 0,
                    borderRight: theme.border.base,
                    fontSize: 200,
                    padding: theme.space.xlarge,
                  },
                }}
              >
                <span
                  css={[
                    StylesRC(theme).underline,
                    {
                      backgroundSize: '100% 12px',
                      display: 'inline-block',
                    },
                  ]}
                >
                  {grade}
                </span>
                <div
                  css={{
                    color: theme.fontColor.base,
                    ...theme.text.small,
                    ...text.expanded(theme),
                    fontWeight: theme.fontWeight.bold,
                    marginTop: theme.space.base,
                  }}
                >
                  Overall Grade
                </div>
              </div>
              <div
                css={{
                  padding: theme.space.large,

                  [theme.mq.medium]: {
                    padding: theme.space.xlarge,
                  },
                }}
              >
                <IntroCopy grade={grade} summary />
                <div
                  css={{
                    fontSize: theme.fontSize.medium,
                    ...theme.text.small,
                    ...text.expanded(theme),
                    fontWeight: theme.fontWeight.bold,
                    marginBottom: theme.space.base,
                  }}
                >
                  Get your full summary
                </div>

                <Formik
                  initialValues={getInitialValues()}
                  validationSchema={reliabilityCalculatorSchema}
                  onSubmit={handleSubmit}
                >
                  {({ isSubmitting }) => (
                    <Form
                      name={formId}
                      data-netlify="true"
                      data-netlify-honeypot="bot-field"
                      noValidate
                    >
                      <div css={{ marginBottom: theme.space.base }}>
                        <FormikField
                          required
                          name="email"
                          as="input"
                          type="email"
                          label="Email"
                        />
                      </div>
                      <div
                        css={{
                          display: 'grid',
                          gap: theme.space.base,
                          marginBottom: theme.space.large,

                          [theme.mq.small]: {
                            gridTemplateColumns: 'repeat(2, 1fr)',
                          },
                        }}
                      >
                        <div>
                          <FormikField
                            required
                            name="firstName"
                            as="input"
                            label="First name"
                          />
                        </div>
                        <div>
                          <FormikField
                            required
                            name="lastName"
                            as="input"
                            label="Last name"
                          />
                        </div>
                        <div>
                          <FormikField
                            required
                            name="company"
                            as="input"
                            label="Company"
                          />
                        </div>
                        <div>
                          <FormikField
                            required
                            name="jobTitle"
                            as="input"
                            label="Job title"
                          />
                        </div>
                      </div>
                      <Button
                        loading={isSubmitting}
                        actionType="submit"
                        type="outlinePrimary"
                      >
                        Submit
                      </Button>
                      <input type="hidden" name="form-name" value={formId} />
                      <BotField name="bot-field" css={misc.hide} type="text" />
                    </Form>
                  )}
                </Formik>
                {response === 'error' && (
                  <Alert type="error">
                    There was an error with your submission. Please try again
                    later.
                  </Alert>
                )}
              </div>
            </div>
          </div>
          <div
            css={{
              textAlign: 'center',
              marginTop: theme.space.large,
            }}
            className="u-contrast"
          >
            <Button onClick={() => window.location.reload()} type="default">
              Test Again
            </Button>
          </div>
        </Fragment>
      ) : (
        <ReliabilityRecommendations url={url} grade={grade} />
      )}
    </ReliabilityCell>
  )
}

Step11.propTypes = {
  location: PropTypes.object.isRequired,
  queryArgs: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
}

export default Step11
